import { type FC, type MouseEventHandler, useState } from "react";

import { useHistory } from "react-router";

import { Button, Icon, Link, Text, toastError } from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetIsLegalEntity,
  useGetOwnerDetails,
  useGetOwners,
  useMutationRequest,
} from "~/v2/api/hooks";
import {
  OPTIONS_LEVELS,
  type OptionsLevel,
  postOptionsApproval,
} from "~/v2/api/rest/account";
import { postAgreement } from "~/v2/api/rest/agreements";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { usePageTimer } from "../../../entityOnboarding/usePageTimer";
import { Layout } from "../../layout";
import { ProgressionButton } from "../../ProgressionButton";
import { AgreementStep } from "../../steps";
import { logAmplitudeEvent, storage } from "../../util";

const CHARACTERISTICS_AND_RISKS_OF_STANDARDIZED_OPTIONS_LINK =
  "https://www.theocc.com/company-information/documents-and-archives/options-disclosure-document";
export const OPTIONS_TRADING_AGREEMENT_LINK =
  "https://files.alpaca.markets/disclosures/library/ALPACA+SECURITIES+LLC++OPTIONS+AGREEMENT.pdf";

export const AgreementPage: FC = () => {
  const history = useHistory();
  const [isAgreementViewed, setIsAgreementViewed] = useState(false);

  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { details } = useGetOwnerDetails();
  const { owners, isLoading: isOwnersLoading } = useGetOwners({
    accountId: currentAccount?.id ?? "",
    options: { enabled: !!currentAccount?.id },
  });
  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  const legalEntityOwner = owners?.find((details) => details.is_legal_entity);

  const accountOpener = owners?.find(
    (details) => details.is_legal_account_opener,
  );

  const pageTimer = usePageTimer("options_agreement_page");

  const mutation = useMutationRequest(
    async (level: OptionsLevel) => {
      await postAgreement({
        ownerId: isLegalEntity
          ? (legalEntityOwner?.owner_id ?? "")
          : (details?.owner_id ?? ""),
        agreement: "options_agreement",
        accountId: currentAccount?.id ?? "",
        signerPartyId: isLegalEntity
          ? (accountOpener?.owner_id ?? "")
          : undefined,
        revision: "01.2024.01",
      });

      return postOptionsApproval(currentAccount?.id ?? "", level);
    },
    {
      onSuccess: () => {
        const pageDuration = pageTimer.stop();
        const totalDuration = storage.endTimer();
        const utms = storage.getUTMs();

        logAmplitudeEvent.pageCompleted(pageDuration);
        logAmplitudeEvent.completed(totalDuration, utms);

        storage.removeTier();
        storage.removeUTMs();

        history.push(Path.ROUTE_OPTIONS_OPT_IN_STATUS);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Error: ${errorMessage}`);
      },
    },
  );

  const onBackClicked: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    pageTimer.stop();
    logAmplitudeEvent.backClicked();
    history.push(Path.ROUTE_OPTIONS_OPT_IN_INVESTMENT_EXPERIENCE);
  };

  const onViewAgreementClicked = () => {
    window.open(OPTIONS_TRADING_AGREEMENT_LINK, "_blank");
    setIsAgreementViewed(true);
  };

  const onSubmit = () => {
    logAmplitudeEvent.nextClicked();

    if (!isAgreementViewed) {
      toastError("Please view the Options Agreement to continue");
      return;
    }

    const level = parseInt(storage.getTier() || "0", 10);

    if (!OPTIONS_LEVELS.includes(level as OptionsLevel)) {
      toastError("Error: Options Tier is not set");
      return;
    }

    mutation.mutate(level as OptionsLevel);
  };

  return (
    <Layout currentStep={AgreementStep}>
      <h2 className="pb-4 font-bold text-xl">Agreement</h2>
      <Text className="pb-4">
        Options involve risk and are not suitable for all investors. Please read
        and agree to the following agreement before you begin trading options.
      </Text>

      <Button variant="secondary" onClick={onViewAgreementClicked}>
        View Options Agreement{" "}
        <Icon name="ArrowTopRightOnSquare" className="ml-2" />
      </Button>

      <Text className="max-w-2xl pt-4">
        By clicking Next you hereby agree to be bound to the terms of the
        Options Trading Agreement as provided to you. Your electronic signature
        will have the same effect as physically signing and returning the
        Options Trading Agreement. I also agree that you have presented to me
        and that I will read the{" "}
        <Link href={CHARACTERISTICS_AND_RISKS_OF_STANDARDIZED_OPTIONS_LINK}>
          Characteristics and Risks of Standardized Options
        </Link>
        .
      </Text>

      <div className="flex max-w-lg items-center justify-between pt-16">
        <ProgressionButton variant="secondary" onClick={onBackClicked}>
          Back
        </ProgressionButton>
        <ProgressionButton
          onClick={onSubmit}
          disabled={
            mutation.isPending ||
            !isAgreementViewed ||
            isOwnersLoading ||
            isLegalEntityLoading
          }
        >
          I Agree
        </ProgressionButton>
      </div>
    </Layout>
  );
};

export default AgreementPage;
